package com.trevorwiebe.timesheet.admin.reporting.domain

import com.trevorwiebe.timesheet.mileage.domain.model.unitNumberOrder
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData

/**
 * One aggregated General Mileage row: total miles driven, gallons burned, and the resulting
 * fuel economy for a single truck.
 */
data class TruckMileageTotal(
    val truckId: String,
    val unitNumber: String,
    val totalMiles: Long,
    val totalGallons: Double,
    val milesPerGallon: Double,
)

/**
 * Aggregates raw mileage entries into per-truck totals for the General Mileage report.
 *
 * Entries are grouped by truck ([MileageEntry.equipmentId]). Within a truck:
 *
 *  - Miles driven are the highest odometer reading minus the lowest. Mileage Log input
 *    validation forbids a later-dated entry from recording a lower odometer than the one
 *    before it, so readings are monotonically increasing in date order and this max−min
 *    form equals the sum of per-segment deltas (and therefore agrees with the IFTA report).
 *  - Gallons are summed across every fuel entry (the same fuel source the IFTA calc uses).
 *  - MPG is miles / gallons, or 0.0 when a truck has no recorded fuel.
 *
 * Trucks with no miles and no gallons are dropped; rows are sorted by unit number.
 */
fun calculateGeneralMileageReport(
    entries: List<MileageEntry>,
    equipment: List<Equipment>,
): List<TruckMileageTotal> {
    return entries.groupBy { it.equipmentId }.map { (truckId, truckEntries) ->
        val odometers = truckEntries.map { it.mileageValue }
        val totalMiles = (odometers.max() - odometers.min())
        val totalGallons = truckEntries.sumOf {
            (it.tripTypeData as? TripTypeData.Fuel)?.gallons ?: 0.0
        }
        TruckMileageTotal(
            truckId = truckId,
            unitNumber = equipment.firstOrNull { it.id == truckId }?.unitNumber ?: truckId,
            totalMiles = totalMiles,
            totalGallons = totalGallons,
            milesPerGallon = if (totalGallons > 0) totalMiles / totalGallons else 0.0,
        )
    }
        .filter { it.totalMiles != 0L || it.totalGallons != 0.0 }
        .sortedWith(compareBy(unitNumberOrder) { it.unitNumber })
}
